import { getHexTileMapKey } from '../hexmap/model/keys';
import { getTile, hexesWithinRadius, isPassable } from '../hexmap/model/map';
import type { HexTileMapKey } from '../hexmap/model/types';
import { pathTo, type Reachable, reachableFrom } from '../hexmap/pathfinding';
import type { RuleSet } from '../rules/schema';
import { invalidateDerived } from './derivedRegistry';
import { buildingCells, distanceTo, growthBonus } from './footprint';
import { buildingLevel, levelStat } from './levels';
import { redirect } from './redirect';
import { disguisedFor, isHidden } from './spies';
import { workerSlots } from './systems/economy';
import { mintOrder } from './territory';
import type { Building, Cell, EntityId, PlayerId, SimContext, SimState, Unit } from './types';

/**
 * Solid occupancy. One unit per tile: a unit may pass through tiles holding
 * its own side's units but only stops on a free one: no other unit standing
 * there, and (when choosing a destination) no other unit's path ending there.
 * Enemy units and rival buildings (every footprint tile, sites too) can't be
 * walked through at all (`blockersFor`; movement.ts waits, re-routes or
 * attacks when one gets in the way). Nobody stands on a completed building's
 * tile: own ones can be walked through, and a unit that stops on one goes
 * *inside* it (`Unit.inside`: hidden, untargetable, not fighting, holding no
 * tile) when it accepts the unit, else steps off to the nearest free tile:
 * - a farm/forestry takes gatherers up to its `workerSlots`;
 * - a city the types in its `accepts` (workers, settlers), any number;
 * - a barracks military units up to its `capacity` (only the types in its
 *   `stores`, when set: soldiers in a barracks, vehicles in a factory).
 * An unfinished site is the exception: its builders stand on it (any number).
 * A grown building's extra footprint tiles count as the building. Units come
 * out onto free tiles around its footprint (`deployUnit`). A hidden unit (an
 * unexposed spy, spies.ts) is a ghost: it holds no tile, blocks nobody and
 * is never moved aside. Pure and deterministic.
 */

/** Furthest a unit looks for a free tile to step onto. */
const SETTLE_RADIUS = 6;
/**
 * Furthest from its building an inside unit comes out; spilled units with
 * nowhere closer die. As far as a new unit may appear (spawn.ts SPAWN_RADIUS).
 */
const DEPLOY_RADIUS = 5;

/** Who wants to stop somewhere: a unit, or one about to be trained (no id yet). */
interface Occupant {
  id?: EntityId;
  owner: PlayerId;
  type: string;
}

interface Occupancy {
  /** Tile → the unit standing still there, outside any building (lowest id first). */
  standing: Map<HexTileMapKey, EntityId>;
  /** Tile → units whose path ends there. */
  heading: Map<HexTileMapKey, EntityId[]>;
  /** Tile → the building on it (every tile of its footprint). */
  buildings: Map<HexTileMapKey, Building>;
  /** Building id → units inside it. */
  inside: Map<EntityId, EntityId[]>;
  /**
   * Tile → the player a disguised spy standing there passes for one of
   * theirs with (spies.ts `disguisedFor`): taken for that player's units
   * only, as a real unit of theirs would be, and free for everyone else.
   */
  ghosts: Map<HexTileMapKey, PlayerId>;
}

/** 'open' ground, a 'site' its builders stand on, or 'inside' a building. */
type StopKind = 'open' | 'site' | 'inside';

function byMint(a: { id: EntityId }, b: { id: EntityId }): number {
  return mintOrder(a.id) - mintOrder(b.id) || (a.id < b.id ? -1 : 1);
}

/** Units in mint order (oldest first). */
function unitsByMint(state: SimState): Unit[] {
  return Object.values(state.units).sort(byMint);
}

function isMilitary(rules: RuleSet, type: string): boolean {
  return !!rules.units[type]?.military;
}

/**
 * Whether units of `type` build sites whose `builtBy` is `builtBy`: that
 * type, or one counting as it (`capAs`) with the `build` action (labourers
 * build what workers build).
 */
function buildsFor(rules: RuleSet, type: string, builtBy: string): boolean {
  if (type === builtBy) return true;
  const def = rules.units[type];
  return def?.capAs === builtBy && def.actions.includes('build');
}

function isMoving(u: Unit): boolean {
  return u.path !== undefined && u.path.length > 0;
}

/**
 * Military units a completed building stores: its level's `capacity` (0 if
 * none) plus `growth.capacityPerExtraTile` per extra footprint tile.
 */
function storageCapacity(rules: RuleSet, b: Building): number {
  const def = rules.buildings[b.type];
  if (!def || b.buildProgress < 1) return 0;
  const base = levelStat(def, buildingLevel(b), 'capacity') ?? 0;
  return base > 0 ? base + growthBonus(rules, b, 'capacityPerExtraTile') : 0;
}

/** Whether building `b` stores units of `type`: it has storage and its `stores` (if any) lists it. */
function storesType(rules: RuleSet, b: Building, type: string): boolean {
  const stores = rules.buildings[b.type]?.stores;
  return (
    isMilitary(rules, type) && storageCapacity(rules, b) > 0 && (!stores || stores.includes(type))
  );
}

/**
 * What a player can see when choosing destinations: rival units, path ends and
 * buildings outside `vision` don't count, so formation gaps never reveal what's
 * under the fog (`settle` sorts out any clash on arrival).
 */
interface Viewer {
  player: PlayerId;
  vision: ReadonlySet<HexTileMapKey>;
}

/** Whether `viewer` (all-seeing when missing) knows about something of `owner`'s on `key`. */
function sees(viewer: Viewer | undefined, owner: PlayerId, key: HexTileMapKey): boolean {
  return !viewer || owner === viewer.player || viewer.vision.has(key);
}

/**
 * Tiles `player`'s paths avoid: every tile of a rival building (sites too)
 * and of a rival unit standing still outside any building (not a hidden
 * one). With `vision`, only those on tiles in it (fog-safe; movement.ts deals
 * with the rest when it gets there).
 */
function blockersFor(
  state: SimState,
  rules: RuleSet,
  player: PlayerId,
  vision?: ReadonlySet<HexTileMapKey>
): Set<HexTileMapKey> {
  const blocked = new Set<HexTileMapKey>();
  const add = (key: HexTileMapKey) => {
    if (!vision || vision.has(key)) blocked.add(key);
  };
  for (const b of Object.values(state.buildings)) {
    if (b.owner === player) continue;
    for (const cell of buildingCells(b)) add(getHexTileMapKey(cell));
  }
  for (const u of Object.values(state.units)) {
    if (u.owner === player || u.inside || isMoving(u) || isHidden(rules, u)) continue;
    add(getHexTileMapKey(u.pos));
  }
  return blocked;
}

/** Where `unit` can walk right now: its terrain, around its owner's blockers (see `blockersFor`). */
function reachOf(
  state: SimState,
  ctx: SimContext,
  unit: { pos: Cell; owner: PlayerId; type: string },
  blocked: ReadonlySet<HexTileMapKey> = blockersFor(state, ctx.rules, unit.owner)
): Reachable {
  const terrain = ctx.rules.units[unit.type]?.terrain ?? [];
  return reachableFrom(ctx.terrain, unit.pos, terrain, blocked);
}

function emptyOccupancy(state: SimState, viewer?: Viewer): Occupancy {
  const buildings = new Map<HexTileMapKey, Building>();
  for (const b of Object.values(state.buildings).sort(byMint)) {
    for (const cell of buildingCells(b)) {
      const key = getHexTileMapKey(cell);
      if (!sees(viewer, b.owner, key)) continue;
      if (!buildings.has(key)) buildings.set(key, b);
    }
  }
  const ghosts = new Map<HexTileMapKey, PlayerId>();
  for (const u of unitsByMint(state)) {
    const victim = disguisedFor(state, u);
    if (victim !== undefined) ghosts.set(getHexTileMapKey(u.pos), victim);
  }
  return { standing: new Map(), heading: new Map(), buildings, inside: new Map(), ghosts };
}

function addHeading(occ: Occupancy, u: Unit, cell: Cell): void {
  const key = getHexTileMapKey(cell);
  const list = occ.heading.get(key);
  if (list) list.push(u.id);
  else occ.heading.set(key, [u.id]);
}

function addInside(occ: Occupancy, buildingId: EntityId, unitId: EntityId): void {
  const list = occ.inside.get(buildingId);
  if (list) list.push(unitId);
  else occ.inside.set(buildingId, [unitId]);
}

/** Drops `u` from the occupancy (e.g. it's being given new orders). */
function forget(occ: Occupancy, u: Unit): void {
  for (const [key, id] of occ.standing) if (id === u.id) occ.standing.delete(key);
  for (const list of [...occ.heading.values(), ...occ.inside.values()]) {
    const i = list.indexOf(u.id);
    if (i >= 0) list.splice(i, 1);
  }
}

/**
 * Who stands, heads or is inside where right now; with `viewer`, only what
 * that player can see (a rival's destination never counts). Hidden units
 * hold no tile, but for a disguised spy's `ghosts` entry.
 */
function occupancyOf(state: SimState, rules: RuleSet, viewer?: Viewer): Occupancy {
  const occ = emptyOccupancy(state, viewer);
  for (const u of unitsByMint(state)) {
    if (isHidden(rules, u)) continue;
    if (u.inside) {
      addInside(occ, u.inside, u.id);
    } else if (u.path && u.path.length > 0) {
      if (viewer && u.owner !== viewer.player) continue;
      addHeading(occ, u, u.path[u.path.length - 1]);
    } else {
      const key = getHexTileMapKey(u.pos);
      if (!sees(viewer, u.owner, key)) continue;
      if (!occ.buildings.has(key) && !occ.standing.has(key)) occ.standing.set(key, u.id);
    }
  }
  return occ;
}

/**
 * How `who` would stop on `cell`, or null when it can't. `reserve`: also
 * count units already heading there (when choosing a destination).
 */
function stopKind(
  rules: RuleSet,
  occ: Occupancy,
  who: Occupant,
  cell: Cell,
  reserve: boolean
): StopKind | null {
  const key = getHexTileMapKey(cell);
  const b = occ.buildings.get(key);
  if (b) return containerKind(rules, occ, b, who, reserve);
  const holder = occ.standing.get(key);
  if (holder !== undefined && holder !== who.id) return null;
  if (occ.ghosts.get(key) === who.owner) return null;
  if (reserve && (occ.heading.get(key) ?? []).some((id) => id !== who.id)) return null;
  return 'open';
}

/** How many units of type `who` building `b` takes inside (0: none; its owner's only). */
function roomFor(rules: RuleSet, b: Building, who: Occupant): number {
  const def = rules.buildings[b.type];
  const unitDef = rules.units[who.type];
  if (b.owner !== who.owner || !def || !unitDef || b.buildProgress < 1) return 0;
  if (def.provides && unitDef.actions.includes('gather')) return workerSlots(rules, b);
  if (def.accepts?.includes(who.type)) return Number.POSITIVE_INFINITY;
  return storesType(rules, b, who.type) ? storageCapacity(rules, b) : 0;
}

function containerKind(
  rules: RuleSet,
  occ: Occupancy,
  b: Building,
  who: Occupant,
  reserve: boolean
): StopKind | null {
  const def = rules.buildings[b.type];
  if (b.owner !== who.owner || !def) return null;
  if (b.buildProgress < 1) {
    return buildsFor(rules, who.type, def.builtBy) && !def.consumesBuilder ? 'site' : null;
  }
  const room = roomFor(rules, b, who);
  if (room === 0) return null;
  let used = (occ.inside.get(b.id) ?? []).filter((id) => id !== who.id).length;
  if (reserve) {
    for (const cell of buildingCells(b)) {
      for (const id of occ.heading.get(getHexTileMapKey(cell)) ?? []) if (id !== who.id) used++;
    }
  }
  return used < room ? 'inside' : null;
}

/**
 * Tiles within `min`..`max` of `around` (or of the nearest of its `footprint`
 * tiles), nearest first, then by q, then r.
 */
function ringOrder(around: Cell, min: number, max: number, footprint?: readonly Cell[]): Cell[] {
  const cells = footprint?.length ? [around, ...footprint] : [around];
  const target = { pos: around, footprint: cells.slice(1) };
  const seen = new Set<HexTileMapKey>();
  const found: Array<{ c: Cell; d: number }> = [];
  for (const from of cells) {
    for (const c of hexesWithinRadius(from, max)) {
      const key = getHexTileMapKey(c);
      if (seen.has(key)) continue;
      seen.add(key);
      const d = distanceTo(target, c);
      if (d >= min) found.push({ c, d });
    }
  }
  return found.sort((a, b) => a.d - b.d || a.c.q - b.c.q || a.c.r - b.c.r).map(({ c }) => c);
}

/**
 * The nearest tile around `around` (and its `footprint`, if given; from `min`
 * to `max` away, ring order) that `who` can walk on and stop on, counting
 * units heading there; with `reach`, only tiles reachable in it; with
 * `kinds`, only tiles it would stop on that way (e.g. open ground, not
 * inside another building).
 */
function nearestFree(
  ctx: SimContext,
  occ: Occupancy,
  who: Occupant,
  around: Cell,
  min: number,
  max: number,
  reach?: Reachable,
  footprint?: readonly Cell[],
  kinds?: readonly StopKind[]
): Cell | undefined {
  const terrain = ctx.rules.units[who.type]?.terrain ?? [];
  for (const cell of ringOrder(around, min, max, footprint)) {
    if (!isPassable(getTile(ctx.terrain, cell), terrain)) continue;
    if (reach && !reach.dist.has(getHexTileMapKey(cell))) continue;
    const kind = stopKind(ctx.rules, occ, who, cell, true);
    if (kind !== null && (!kinds || kinds.includes(kind))) return cell;
  }
  return undefined;
}

/**
 * The own completed buildings storing military units (barracks, factories) in
 * city `cityId`'s territory, oldest first.
 */
function cityStores(
  state: SimState,
  rules: RuleSet,
  cityId: EntityId,
  owners: ReadonlyMap<HexTileMapKey, EntityId>
): Building[] {
  const city = state.buildings[cityId];
  if (!city) return [];
  return Object.values(state.buildings)
    .filter(
      (b) =>
        b.owner === city.owner &&
        storageCapacity(rules, b) > 0 &&
        owners.get(getHexTileMapKey(b.pos)) === cityId
    )
    .sort(byMint);
}

/**
 * A store (barracks, factory) of city `cityId` that takes `who`'s type and has
 * room for it (counting units heading in), if any.
 */
function storeWithRoom(
  state: SimState,
  rules: RuleSet,
  occ: Occupancy,
  who: Occupant,
  cityId: EntityId,
  owners: ReadonlyMap<HexTileMapKey, EntityId>
): Building | undefined {
  return cityStores(state, rules, cityId, owners).find(
    (b) => stopKind(rules, occ, who, b.pos, true) === 'inside'
  );
}

/** Puts `unit` inside building `b`: hidden on its tile until it comes out. */
function enterBuilding(unit: Unit, b: Building): void {
  unit.inside = b.id;
  unit.pos = { q: b.pos.q, r: b.pos.r };
  delete unit.path;
  delete unit.blocked;
  unit.progress = 0;
  if (unit.order?.kind === 'move' || unit.order?.kind === 'attackMove') delete unit.order;
}

/**
 * Brings a unit that's inside a building out onto the nearest free tile
 * within DEPLOY_RADIUS of any tile of the building's footprint (`min` 0 also
 * allows the building's own tile, once it's gone; `fallenFootprint` then
 * gives the extra tiles it covered). Returns false (the unit stays inside)
 * when there's none.
 */
function deployUnit(
  state: SimState,
  ctx: SimContext,
  occ: Occupancy,
  unit: Unit,
  min = 1,
  fallenFootprint?: readonly Cell[]
): boolean {
  if (!unit.inside) return true;
  const reach = reachOf(state, ctx, unit);
  const who = { id: unit.id, owner: unit.owner, type: unit.type };
  const home = occ.buildings.get(getHexTileMapKey(unit.pos));
  const footprint = fallenFootprint ?? (home?.id === unit.inside ? home.footprint : undefined);
  const spot = nearestFree(ctx, occ, who, unit.pos, min, DEPLOY_RADIUS, reach, footprint, ['open']);
  if (!spot) return false;
  forget(occ, unit);
  delete unit.inside;
  redirect(state, unit, pathTo(reach, spot));
  addHeading(occ, unit, spot);
  invalidateDerived(state);
  return true;
}

/** Deploys every unit inside building `buildingId` (oldest first). */
function deployAll(state: SimState, ctx: SimContext, occ: Occupancy, buildingId: EntityId): void {
  for (const u of unitsByMint(state)) {
    if (u.inside === buildingId) deployUnit(state, ctx, occ, u);
  }
}

/**
 * After movement: every still unit must be somewhere it can stop. Units that
 * were already standing keep their tiles (oldest first), then units that
 * stopped this tick (`stopped`: arrived, or had a path at the start of the
 * tick and lost it, e.g. automation giving up mid-route) claim theirs in id
 * order, so a unit that just stopped gives way. A unit stopping on an own
 * building that takes it goes inside; one that can't stay walks to the
 * nearest free tile around it. Units inside a building that's gone (or no
 * longer theirs) are released where they are. Hidden units are left alone.
 */
function settle(state: SimState, ctx: SimContext, stopped: ReadonlySet<EntityId>): void {
  const { rules } = ctx;
  const occ = emptyOccupancy(state);
  const still: Unit[] = [];
  const late: Unit[] = [];
  let blocked: Map<PlayerId, Set<HexTileMapKey>> | undefined;
  for (const u of unitsByMint(state)) {
    // A ghost stays where it stops (spies.ts).
    if (isHidden(rules, u) && !u.inside) continue;
    if (u.inside) {
      const b = state.buildings[u.inside];
      if (b && b.owner === u.owner && b.buildProgress >= 1) {
        addInside(occ, b.id, u.id);
        continue;
      }
      delete u.inside;
    }
    if (isMoving(u)) addHeading(occ, u, (u.path as Cell[])[(u.path as Cell[]).length - 1]);
    else if (stopped.has(u.id)) late.push(u);
    else still.push(u);
  }
  for (const u of [...still, ...late]) {
    const who = { id: u.id, owner: u.owner, type: u.type };
    let kind = stopKind(rules, occ, who, u.pos, false);
    // A fighter on an attack order fights, and a healer on a heal order heals, from outside.
    if (kind === 'inside' && (u.order?.kind === 'attack' || u.order?.kind === 'heal')) kind = null;
    if (kind === 'open') {
      occ.standing.set(getHexTileMapKey(u.pos), u.id);
    } else if (kind === 'inside') {
      const b = occ.buildings.get(getHexTileMapKey(u.pos));
      if (b) {
        enterBuilding(u, b);
        addInside(occ, b.id, u.id);
      }
    } else if (kind === null) {
      blocked ??= new Map();
      let mine = blocked.get(u.owner);
      if (!mine) {
        mine = blockersFor(state, ctx.rules, u.owner);
        blocked.set(u.owner, mine);
      }
      const reach = reachOf(state, ctx, u, mine);
      const spot = nearestFree(ctx, occ, who, u.pos, 1, SETTLE_RADIUS, reach);
      if (!spot) continue; // Nowhere to go: stay put.
      redirect(state, u, pathTo(reach, spot));
      addHeading(occ, u, spot);
    }
  }
}

export type { Occupancy, Occupant, StopKind, Viewer };
export {
  addHeading,
  blockersFor,
  buildsFor,
  cityStores,
  deployAll,
  deployUnit,
  enterBuilding,
  forget,
  isMilitary,
  nearestFree,
  occupancyOf,
  reachOf,
  ringOrder,
  roomFor,
  SETTLE_RADIUS,
  settle,
  stopKind,
  storageCapacity,
  storesType,
  storeWithRoom,
  unitsByMint,
};
